import {Material} from '@domain/materials'
import {EntityConfigDto, TreeNode} from '@hconnect/common/types'
import {Stack} from '@mui/material'
import {LocalizationProvider} from '@mui/x-date-pickers'
import {AdapterMoment} from '@mui/x-date-pickers/AdapterMoment'
import React, {useEffect, useMemo} from 'react'
import {useForm} from 'react-hook-form'

import {useUrlParam} from '../../../routing'
import {Equipment} from '../../../upm/types/equipment'
import {getFieldIsDisabled} from '../../helpers/getFieldIsDisabled'
import {
  ACTIVE_STATUS_ID,
  MOTHBALLED_STATUS_ID,
  useUpdateDepartmentStatus
} from '../../hooks/upmEntityConfig/useUpdateDepartmentStatus'
import {ItemDetailsLayout} from '../../plant-setup-item-layout'
import {useItemDetails} from '../../providers'
import {Config, configFieldsMap, ConfigType, Field} from '../../types'

import {ConfigControlledField} from './ConfigControlledField'
import {serializeFormValue} from './utils/formDataSerializer'

interface ConfigFormProps {
  selectedItem: TreeNode
  configNotCreated: boolean
  config: EntityConfigDto
  onSubmit: (data: EntityConfigDto) => Promise<void>
  materials: Material[]
  type: ConfigType
  showAllMaterials?: boolean
  departmentDetails?: Equipment
}

export const ConfigForm: React.FC<ConfigFormProps> = ({
  selectedItem,
  onSubmit,
  config,
  configNotCreated,
  materials,
  type,
  departmentDetails: department
}) => {
  const {mutateAsync: updateDepartmentStatus} = useUpdateDepartmentStatus()

  const hasMothballedField = configFieldsMap[type].some((field) => field.name === 'mothballed')

  const defaultValues = useMemo(() => {
    const defaultConfig = {
      localName: '',
      englishName: '',
      workCenter: '',
      commissEndDate: '',
      mothballed: 'false',
      commissStartDate: ''
    } as Config

    const shouldReturnDefaultConfig =
      configNotCreated ||
      !config ||
      !config.payload?.productionData ||
      config.payload.productionData.length === 0

    if (shouldReturnDefaultConfig) {
      return defaultConfig
    }

    const configData = config.payload.productionData.reduce(
      (acc, item) => {
        acc[item.name] = item.value
        return acc
      },
      {} as Record<string, string>
    ) as Config

    if (hasMothballedField && department) {
      configData.mothballed = department.statusId === MOTHBALLED_STATUS_ID ? 'true' : 'false'
    }

    return configData
  }, [configNotCreated, config, department, hasMothballedField])

  const {
    handleSubmit,
    control,
    reset,
    watch,
    clearErrors,
    trigger,
    formState: {isDirty, isSubmitSuccessful, isSubmitting, isValid}
  } = useForm({
    mode: 'all',
    shouldFocusError: false,
    defaultValues
  })

  const watchStartDate = watch('commissStartDate')
  const watchEndDate = watch('commissEndDate')
  const watchMothballed = watch('mothballed')

  useEffect(() => {
    trigger()
  }, [watchMothballed, trigger])

  const {userPermissions, latestData} = useItemDetails()
  const plantCode = useUrlParam('plantCode')

  useEffect(() => {
    if (isSubmitSuccessful) {
      reset(defaultValues)
    }
  }, [isSubmitSuccessful, defaultValues, reset])

  const submit = async (data: Config) => {
    if (!isDirty) return
    const newConfig = structuredClone(config)

    newConfig.payload.productionData = Object.keys(data)
      .filter((key) => key !== 'mothballed')
      .map((key) => ({
        name: key,
        value: serializeFormValue(data[key])
      }))

    if (hasMothballedField && department) {
      const isMothballed = data.mothballed === 'true'
      const newStatusId = isMothballed ? MOTHBALLED_STATUS_ID : ACTIVE_STATUS_ID
      if (department.statusId !== newStatusId) {
        await updateDepartmentStatus({
          ...department,
          statusId: newStatusId
        })
      }
    }

    await onSubmit(newConfig)
  }

  return (
    <LocalizationProvider dateAdapter={AdapterMoment}>
      <ItemDetailsLayout
        saveButton={{
          hide: false,
          onClick: handleSubmit(submit),
          disabled: isSubmitting || !isDirty || !isValid,
          ButtonProps: {
            loadingPosition: 'start',
            loading: isSubmitting
          }
        }}
      >
        <Stack maxWidth="500px" spacing={3}>
          {configFieldsMap[type].map((field: Field) => {
            const disabled = getFieldIsDisabled(
              field.editPermissionRequired || [],
              latestData,
              userPermissions || [],
              plantCode
            )
            const ignoreValidation = watchMothballed === 'true'
            const isOptional = field.optional || ignoreValidation

            return (
              <ConfigControlledField
                upmId={selectedItem.upmId}
                clearErrors={clearErrors}
                disabled={disabled}
                control={control}
                field={field}
                materials={materials}
                key={`${field.label}`}
                startDate={watchStartDate}
                endDate={watchEndDate}
                ignoreValidation={ignoreValidation}
                isOptional={isOptional}
              />
            )
          })}
        </Stack>
      </ItemDetailsLayout>
    </LocalizationProvider>
  )
}
